import { memo, useCallback } from 'react';
import { makeStyles, tokens, Text, mergeClasses } from '@fluentui/react-components';
import { FlashFilled, PlayFilled, BranchForkFilled } from '@fluentui/react-icons';
import type { OperationDefinition } from '@/types/operations';
import { OperationInfoButton } from './OperationInfoButton';
import { OperationFavoriteButton } from './OperationFavoriteButton';
import { operationRowRevealStyle } from './operationRowReveal';

type OperationCardProps = {
  operation: OperationDefinition;
  onClick: (operation: OperationDefinition, event?: React.MouseEvent) => void;
  isSelected?: boolean;
};

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    minHeight: '40px',
    transitionProperty: 'background-color, box-shadow, transform',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease',
    width: '100%',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow2,
    },
    ':active': {
      transform: 'scale(0.97)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
  },
  cardSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  iconContainer: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusSmall,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  connectorIcon: {
    width: '14px',
    height: '14px',
    borderRadius: tokens.borderRadiusSmall,
    objectFit: 'contain' as const,
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  name: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  connectorName: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Reveal the favorite + info buttons on card hover / when any descendant is focused.
  cardHoverReveal: operationRowRevealStyle,
});

/**
 * Get default icon based on operation category
 */
const getDefaultIcon = (category: string) => {
  switch (category) {
    case 'trigger':
      return <FlashFilled />;
    case 'control':
      return <BranchForkFilled />;
    default:
      return <PlayFilled />;
  }
};

/**
 * Card component displaying a single operation in the picker.
 *
 * Rendered as `<div role="button">` (not a native `<button>`) so the inner
 * info/favorite controls — which are themselves focusable role="button"
 * spans — don't create an invalid `<button>`-inside-`<button>` HTML tree.
 * Keyboard activation (Enter / Space) is wired up manually to match native
 * button behavior, including `preventDefault` on Space to suppress page
 * scroll.
 */
export const OperationCard = memo(({ operation, onClick, isSelected = false }: OperationCardProps) => {
  const styles = useStyles();

  const handleClick = useCallback((e: React.MouseEvent) => {
    onClick(operation, e);
  }, [onClick, operation]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only activate on the card itself, not when keys bubble up from the
    // nested info / favorite controls (those handle their own activation
    // and stop propagation, but guard defensively).
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(operation);
    }
  }, [onClick, operation]);

  const brandColor = operation.brandColor || '#0078D4';
  const hasConnectorIcon = operation.icon && operation.icon.startsWith('http');

  return (
    <div
      role="button"
      tabIndex={0}
      className={mergeClasses(
        styles.card,
        styles.cardHoverReveal,
        isSelected && styles.cardSelected,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`Add ${operation.name}`}
      aria-pressed={isSelected}
    >
      <div
        className={styles.iconContainer}
        style={{ backgroundColor: brandColor }}
      >
        {hasConnectorIcon ? (
          <img src={operation.icon} alt="" className={styles.connectorIcon} />
        ) : (
          <span className={styles.icon}>{getDefaultIcon(operation.category)}</span>
        )}
      </div>
      <div className={styles.content}>
        <Text className={styles.name}>{operation.name}</Text>
        {operation.connectorName && (
          <Text className={styles.connectorName}>{operation.connectorName}</Text>
        )}
      </div>
      <OperationInfoButton operation={operation} />
      <OperationFavoriteButton operation={operation} />
    </div>
  );
});

export default OperationCard;

